import { useState, useEffect, type FormEvent } from 'react';
import type { NewPetPayload } from '../hooks/usePets';
import type { AsyncStatus } from '../types';
import { PET_SPECIES } from '../utils/constants';
import { UpgradeRequiredError } from '../utils/upgradeErrors';

interface AddPetFormProps {
  onSubmit: (payload: NewPetPayload) => Promise<void>;
  onClose: () => void;
}

const SPECIES_EMOJI: Record<string, string> = {
  dog: '🐶', cat: '🐱', bird: '🐦', rabbit: '🐰', other: '🐾',
};

const SPECIES_PLACEHOLDERS: Record<
  NewPetPayload['species'],
  { name: string; breed: string; weight: string }
> = {
  dog: {
    name: 'e.g. Bruno',
    breed: 'e.g. Golden Retriever',
    weight: 'e.g. 12.5',
  },
  cat: {
    name: 'e.g. Luna',
    breed: 'e.g. Persian',
    weight: 'e.g. 4.2',
  },
  bird: {
    name: 'e.g. Rio',
    breed: 'e.g. Cockatiel',
    weight: 'e.g. 0.08',
  },
  rabbit: {
    name: 'e.g. Thumper',
    breed: 'e.g. Holland Lop',
    weight: 'e.g. 1.8',
  },
  other: {
    name: 'e.g. Coco',
    breed: 'e.g. Mixed breed',
    weight: 'e.g. 5',
  },
};

/**
 * AddPetForm — full-screen modal that collects pet details and
 * calls the `addPet` function from the usePets hook.
 *
 * Schema alignment (docs/technical.md):
 *   name · breed · species · date_of_birth · weight
 */
function AddPetForm({ onSubmit, onClose }: AddPetFormProps) {
  const [name, setName] = useState('');
  const [breed, setBreed] = useState('');
  const [species, setSpecies] = useState<NewPetPayload['species']>('dog');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [weight, setWeight] = useState('');
  const [status, setStatus] = useState<AsyncStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  // Trap focus inside modal when open (accessibility)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg('');

    // Basic validation
    if (!name.trim()) { setErrorMsg('Pet name is required.'); return; }
    if (!breed.trim()) { setErrorMsg('Breed is required.'); return; }
    if (!dateOfBirth)  { setErrorMsg('Date of birth is required.'); return; }

    const parsedWeight = weight ? parseFloat(weight) : null;
    if (weight && (isNaN(parsedWeight!) || parsedWeight! <= 0)) {
      setErrorMsg('Weight must be a positive number.');
      return;
    }

    setStatus('loading');

    try {
      await onSubmit({
        name: name.trim(),
        breed: breed.trim(),
        species,
        date_of_birth: dateOfBirth,
        weight: parsedWeight,
      });
      // Parent will unmount this component on success
    } catch (err) {
      if (UpgradeRequiredError.is(err)) {
        setStatus('idle');
        return;
      }
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong.');
      setStatus('error');
    }
  };

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-pet-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Sheet / modal */}
      <div className="w-full sm:max-w-md bg-brand-900 border border-white/10 rounded-t-3xl sm:rounded-3xl p-6 animate-slide-up max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 id="add-pet-title" className="font-display text-xl font-bold text-white">
            Add a new pet
          </h2>
          <button
            id="close-add-pet"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 flex items-center justify-center rounded-xl text-white/50 hover:text-white hover:bg-white/10 transition-all"
          >
            ✕
          </button>
        </div>

        <form id="add-pet-form" onSubmit={(e) => void handleSubmit(e)} noValidate className="space-y-4">

          {/* Species picker */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">Species</label>
            <div className="grid grid-cols-5 gap-2">
              {PET_SPECIES.map((s) => (
                <button
                  key={s}
                  type="button"
                  id={`species-${s}`}
                  onClick={() => setSpecies(s)}
                  className={`flex flex-col items-center gap-1 py-2 px-1 rounded-xl border text-xs font-medium transition-all duration-200 ${
                    species === s
                      ? 'bg-brand-500 border-brand-400 text-white'
                      : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
                  }`}
                >
                  <span className="text-xl">{SPECIES_EMOJI[s]}</span>
                  <span className="capitalize">{s}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Name */}
          <div>
            <label htmlFor="pet-name" className="block text-sm font-medium text-white/80 mb-1">
              Name <span className="text-red-400">*</span>
            </label>
            <input
              id="pet-name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={SPECIES_PLACEHOLDERS[species].name}
              className="input-field"
            />
          </div>

          {/* Breed */}
          <div>
            <label htmlFor="pet-breed" className="block text-sm font-medium text-white/80 mb-1">
              Breed <span className="text-red-400">*</span>
            </label>
            <input
              id="pet-breed"
              type="text"
              required
              value={breed}
              onChange={(e) => setBreed(e.target.value)}
              placeholder={SPECIES_PLACEHOLDERS[species].breed}
              className="input-field"
            />
          </div>

          {/* Date of Birth */}
          <div>
            <label htmlFor="pet-dob" className="block text-sm font-medium text-white/80 mb-1">
              Date of Birth <span className="text-red-400">*</span>
            </label>
            <input
              id="pet-dob"
              type="date"
              required
              value={dateOfBirth}
              max={new Date().toISOString().split('T')[0]}
              onChange={(e) => setDateOfBirth(e.target.value)}
              className="input-field [color-scheme:dark]"
            />
          </div>

          {/* Weight */}
          <div>
            <label htmlFor="pet-weight" className="block text-sm font-medium text-white/80 mb-1">
              Weight (kg)
              <span className="ml-1 text-white/30 font-normal">optional</span>
            </label>
            <input
              id="pet-weight"
              type="number"
              min="0.1"
              step="0.1"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              placeholder={SPECIES_PLACEHOLDERS[species].weight}
              className="input-field"
            />
          </div>

          {/* Error */}
          {errorMsg && (
            <div role="alert" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
              {errorMsg}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              id="cancel-add-pet"
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              id="submit-add-pet"
              disabled={status === 'loading'}
              className="btn-primary flex-1 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status === 'loading' ? (
                <>
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
                  Saving…
                </>
              ) : (
                'Add pet'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddPetForm;
